import { GRID_BORDER_WIDTH, GRID_TILE_SIZE } from './gridSnap'

export interface GridGeometry {
  cols: number
  rows: number
  tileSize: number
  width: number
  height: number
  isDesignWidth: boolean
}

export interface ResolveGridGeometryOptions {
  availableWidth: number
  cols: number
  rows: number
  minCols?: number
  tileSize?: number
}

const clamp = (value: number, min: number, max: number): number =>
  Math.min(Math.max(value, min), max)

export function resolveGridGeometry({
  availableWidth,
  cols: designCols,
  rows,
  minCols = 1,
  tileSize: designTileSize = GRID_TILE_SIZE,
}: ResolveGridGeometryOptions): GridGeometry {
  const maxCols = Math.max(1, Math.floor(designCols))
  const floorCols = clamp(Math.floor(minCols), 1, maxCols)
  const designWidth = maxCols * designTileSize + GRID_BORDER_WIDTH

  const designGeometry: GridGeometry = {
    cols: maxCols,
    rows,
    tileSize: designTileSize,
    width: designWidth,
    height: rows * designTileSize + GRID_BORDER_WIDTH,
    isDesignWidth: true,
  }

  if (!Number.isFinite(availableWidth) || availableWidth >= designWidth) {
    return designGeometry
  }

  const tileSpace = availableWidth - GRID_BORDER_WIDTH
  if (tileSpace <= 0) return designGeometry

  const cols = clamp(Math.round(tileSpace / designTileSize), floorCols, maxCols)
  const tileSize = tileSpace / cols

  return {
    cols,
    rows,
    tileSize,
    width: availableWidth,
    height: rows * tileSize + GRID_BORDER_WIDTH,
    isDesignWidth: false,
  }
}

// Below `md` the tile-canvas modalities (Flip Cards, Multi-Image's reveal) paint
// a 12-column board that fills the chat column. `resolveGridGeometry` on its
// own stops growing at the 12 × 32px design width, so the tile is derived from
// the column instead and the design width is made equal to it.
export const COMPACT_CANVAS_COLS = 12

export const resolveCompactCanvasGeometry = ({
  contentWidthPx,
  rows,
}: {
  contentWidthPx: number
  rows: number
}): GridGeometry =>
  resolveGridGeometry({
    availableWidth: contentWidthPx,
    cols: COMPACT_CANVAS_COLS,
    rows,
    minCols: COMPACT_CANVAS_COLS,
    tileSize: (contentWidthPx - GRID_BORDER_WIDTH) / COMPACT_CANVAS_COLS,
  })

export interface GridZone {
  startCol: number
  startRow: number
  endCol: number
  endRow: number
}

export interface GridAxes {
  cols: number
  rows: number
}

export function scaleGridZone(
  zone: GridZone,
  from: GridAxes,
  to: GridAxes,
): GridZone {
  const scale = (
    value: number,
    fromExtent: number,
    toExtent: number,
  ): number => {
    if (fromExtent <= 0) return 0
    return clamp(
      Math.round((value * toExtent) / fromExtent),
      0,
      Math.max(0, toExtent - 1),
    )
  }

  const startCol = scale(zone.startCol, from.cols, to.cols)
  const startRow = scale(zone.startRow, from.rows, to.rows)

  return {
    startCol,
    startRow,
    endCol: Math.max(startCol, scale(zone.endCol, from.cols, to.cols)),
    endRow: Math.max(startRow, scale(zone.endRow, from.rows, to.rows)),
  }
}
